import React from 'react';

const deployItems = [
  {
    title: 'Supervisor-routed agents',
    description: 'LangGraph supervisor routes to specialist agents: product, account, eligibility and general queries.',
  },
  {
    title: 'Hybrid retrieval with citations',
    description: 'BM25 plus dense vector plus cross-encoder reranking. Approved sources only, every answer cited.',
  },
  {
    title: 'Input and output guardrails',
    description: 'Prompt-injection detection, PII redaction, regulated-advice classifiers and structured-output validation.',
  },
  {
    title: 'Confidence-gated escalation',
    description: 'Low-confidence answers go to a human with the full trace, context and a suggested reply.',
  },
];

const safeguards = [
  'Full conversation audit trail. Every answer logged and auditable.',
  'PII detected and redacted automatically.',
  'Zero-retention endpoints. Your data never trains a model.',
  'Eval suite runs before every release.',
];

const AdvisePage: React.FC = () => {
  return (
    <>
      <section className="section-padding bg-white">
        <div className="container-max">
          <p className="text-sm text-neutral-500 mb-3">Zwift Advise</p>
          <h1 className="text-4xl md:text-5xl font-semibold text-black leading-[1.05] tracking-[-0.02em] mb-5 max-w-3xl">
            Customer AI that stays within compliance rules.
          </h1>
          <p className="text-lg text-neutral-600 leading-relaxed max-w-2xl mb-6">
            Answers like your best adviser and never says what compliance would not allow.
          </p>
          <div className="flex flex-wrap gap-2">
            {['GDPR', 'SOC 2', 'EU AI Act'].map((tag) => (
              <span key={tag} className="inline-flex items-center text-xs font-medium text-neutral-500 bg-neutral-100 px-2.5 py-1 rounded-full">
                {tag}
              </span>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-neutral-50">
        <div className="container-max">
          <h2 className="text-3xl font-semibold text-black tracking-tight mb-10">What we deploy</h2>
          <div className="grid md:grid-cols-2 gap-5">
            {deployItems.map((item, i) => (
              <div key={item.title} className="bg-white rounded-2xl p-6 border border-neutral-200">
                <span className="text-xs font-medium text-neutral-300 block mb-3">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <h3 className="text-lg font-semibold text-black mb-2">{item.title}</h3>
                <p className="text-neutral-600 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="container-max">
          <h2 className="text-3xl font-semibold text-black tracking-tight mb-8">Safeguards</h2>
          <ul className="space-y-4 max-w-2xl">
            {safeguards.map((s) => (
              <li key={s} className="flex items-start gap-3">
                <svg className="w-5 h-5 text-black shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-neutral-600 leading-relaxed">{s}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="section-padding bg-neutral-50">
        <div className="container-max">
          <h2 className="text-3xl font-semibold text-black tracking-tight mb-4">Used in</h2>
          <div className="flex flex-wrap gap-2 mb-10">
            {['Consumer fintech', 'Banking', 'Insurance', 'Payments', 'Wealth management'].map((industry) => (
              <span key={industry} className="inline-flex items-center text-sm font-medium text-neutral-600 bg-white border border-neutral-200 px-3 py-1.5 rounded-full">
                {industry}
              </span>
            ))}
          </div>

          <blockquote className="bg-white rounded-2xl p-8 border border-neutral-200 max-w-2xl">
            <p className="text-neutral-700 leading-relaxed mb-4">
              "The agentic, multi-agent architecture delivered strong accuracy and robustness. Supervisor routing, specialist agents, retrieval and I/O guardrails were all built to production standard."
            </p>
            <footer className="text-sm text-neutral-500">
              Adam Murphy, LLM Engineer, AWS ML Certified, Gains
            </footer>
          </blockquote>
        </div>
      </section>

      <section className="section-padding bg-black">
        <div className="container-max">
          <h2 className="text-3xl font-semibold text-white mb-4">
            Ready to discuss Zwift Advise?
          </h2>
          <p className="text-neutral-400 mb-8 max-w-xl">
            Book a free fit call. We will check whether Zwift Advise is the right fit and tell you honestly.
          </p>
          <a
            href="https://calendar.app.google/cnN9KoLp6WZYMX9G7"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center bg-white hover:bg-neutral-100 text-black font-medium px-7 py-3.5 rounded-full transition-colors"
          >
            Book a fit call to discuss Zwift Advise
          </a>
        </div>
      </section>
    </>
  );
};

export default AdvisePage;
